import { useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { useSEO } from "@/hooks/use-seo";
import { seoFor } from "@/data/marketing-routes";
import { TERMS_PRICING_ANCHOR } from "@/lib/marketing/links";

type LatestTos = {
  effectiveAt: string;
  versionId: string;
  label: string;
  changeSummary: string;
  fullText: string;
};

export default function TermsPage() {
  useSEO(seoFor("/terms"));
  // Task #628: the dated header, the "what changed" callout, AND the full
  // legal body all come from the latest row in the `tos_versions` table.
  // The boot-time seeder ensures this endpoint always returns something
  // (the legacy `TOS_LATEST_FULL_TEXT` constant is used as a one-time
  // bootstrap if the table is empty).
  const { data: latest, isLoading } = useQuery<LatestTos>({
    queryKey: ["/api/tos/latest"],
  });
  const bodyLoaded = Boolean(latest?.fullText);
  const [location] = useLocation();
  // Re-run once the legal body loads: it renders above the anchored sections
  // and would otherwise push a deep-linked section (#sms, #pricing-…) down.
  useEffect(() => {
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location, bodyLoaded]);


  const lastUpdatedLabel = latest?.label ?? "—";
  const changeSummary = latest?.changeSummary ?? "";
  const fullText = latest?.fullText ?? "";

  return (
    <div className="flex flex-col min-h-screen bg-white" data-testid="terms-page">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="pt-28 md:pt-32 pb-12 md:pb-16">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-tight" data-testid="text-terms-title">Terms of Service</h1>
            <p className="text-blue-200 text-sm" data-testid="text-terms-date">Last updated: {lastUpdatedLabel}</p>
          </div>
        </section>
      </div>

      <main className="flex-grow container mx-auto px-6 py-12 max-w-4xl">
        <div className="prose prose-lg max-w-none text-gray-600 space-y-6 prose-headings:text-gray-900 prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-a:text-brand prose-strong:text-gray-900">
          {changeSummary && (
            <div
              className="not-prose rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900"
              data-testid="callout-tos-latest-changes"
            >
              <p className="font-semibold mb-1">What changed in this update ({lastUpdatedLabel})</p>
              <p className="text-blue-800" data-testid="text-tos-change-summary">
                {changeSummary}
              </p>
            </div>
          )}
          {isLoading && !fullText && (
            <p className="text-gray-500" data-testid="text-tos-loading">Loading the latest Terms of Service…</p>
          )}
          {fullText && (
            <div data-testid="text-tos-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{fullText}</ReactMarkdown>
            </div>
          )}

          <section id={TERMS_PRICING_ANCHOR} className="scroll-mt-28 pt-6" data-testid="section-pricing-terms">
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Pricing, trials, refunds and lifetime plans</h2>
            <p>
              <strong>Pricing changes.</strong> We review prices periodically. We will email you at least 30 days before a
              price change takes effect. A price change never applies in the middle of a paid term; it applies from your
              next renewal.
            </p>
            <p>
              <strong>Free trial.</strong> Host and Property Manager plans start with a 14-day free trial. You don't need a
              payment method to start. If you haven't added one by the end of the trial, your account is paused and becomes
              read-only, and we keep your data for 30 days.
            </p>
            <p>
              <strong>Cancellations and refunds.</strong> You can cancel a monthly plan at any time; it won't renew, and we
              don't refund partial months. Annual plans and lifetime purchases can be refunded in full within 30 days of
              payment.
            </p>
            <p>
              <strong>Lifetime plans.</strong> A lifetime plan gives one account access to the plan's included features for
              a set number of properties (the "allowance") for as long as PadPulse operates the service. Properties beyond
              the allowance are billed at the standard rates in effect at the time. Lifetime plans can't be transferred to
              another person or account. If we ever discontinue the service, we will give lifetime customers at least 90
              days' notice and a way to export their data.
            </p>
            <p data-testid="paragraph-syre-event-terms">
              <strong>SYRE event offer.</strong> Event purchases are refundable through November 30.
              The property-lock bonus is reserved only when payment is completed by October 25; signing up alone
              does not reserve it. Sales and Q&amp;A close October 31.
            </p>
            <p>
              SYRE includes private Slack support and manual onboarding within one business day after payment. Access
              follows account creation and verification, one-time checkout, and server confirmation of payment; setup
              begins after activation. Property locks are reserved and are not yet shipped, with delivery estimated for
              Q2 2027. If a reserved lock has not shipped by September 30, 2027, the account receives $100 in PadPulse
              account credit for each undelivered lock. Credit has no cash value. A refund invalidates the contribution
              and any related lock credit.
            </p>
          </section>

          <section id="sms" className="scroll-mt-28 pt-6" data-testid="section-sms-terms">
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">SMS Terms and Conditions</h2>
            <p className="text-sm text-gray-500" data-testid="text-sms-terms-date">Last updated: April 10, 2026</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Program Name</h3>
            <p>PadPulse (operated by PadPulse, a service supporting PadSplit hosts).</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Program Description</h3>
            <p>PadPulse provides SMS messages to PadSplit hosts and users who opt in. Messages may include account notifications, member and property updates, payment and occupancy alerts, maintenance reminders, support communications, and other information related to managing your PadSplit properties through the PadPulse platform.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Message Frequency</h3>
            <p>Message frequency varies based on your account activity and the notifications you enable. You may receive messages as events occur on your account.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Message and Data Rates</h3>
            <p>Message and data rates may apply. PadPulse does not charge for SMS messages, but your mobile carrier's standard messaging and data rates will apply to each message sent or received. Contact your wireless provider for details about your messaging plan.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Opt-In</h3>
            <p>By providing your mobile phone number to PadPulse and agreeing to receive text messages, you consent to receive SMS messages from PadPulse at the number provided. Consent is not a condition of purchase.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Opt-Out Instructions</h3>
            <p>You can cancel the SMS service at any time. Simply text <strong>STOP</strong> to the short code or number from which you are receiving messages. After you send <strong>STOP</strong>, we will send you a confirmation message that you have been unsubscribed. You will no longer receive SMS messages from us. If you want to rejoin, sign up as you did the first time and we will start sending messages to you again.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Help</h3>
            <p>If you are experiencing issues with the messaging program, reply with the keyword <strong>HELP</strong> for more assistance, or contact our support team directly using the information below.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Support Contact Information</h3>
            <p>
              For questions or support regarding the PadPulse SMS program, please contact us at:<br />
              Email: <a href="mailto:info@padpulse.io" className="text-brand underline">info@padpulse.io</a><br />
              Website: <a href="https://padpulse.io" className="text-brand underline">https://padpulse.io</a>
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Carriers</h3>
            <p>Carriers are not liable for delayed or undelivered messages. Supported carriers include AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost, U.S. Cellular, MetroPCS, and others.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Privacy</h3>
            <p>We respect your privacy. We will not sell, rent, or share your mobile number or personal information with third parties for marketing purposes. For more information, please review our Privacy Policy at <a href="/privacy" className="text-brand underline">padpulse.io/privacy</a>.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Changes to Terms</h3>
            <p>PadPulse reserves the right to change or terminate the SMS program at any time. We will notify subscribers of any material changes via the messaging program or by posting updates on this page.</p>
          </section>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
